import {
    daysFromToday,
    formatCompactDate,
    shiftIso,
    todayIso,
    toIsoDate,
} from '../../utils/dates'

export { daysFromToday, shiftIso, todayIso }

/** Mismas ventanas de vencimiento que la cobranza anterior: días relativos a hoy, inclusive. */
export const DUE_RANGES = [
    { id: 'todos', label: 'Todos', from: -60, to: 60, neutral: true },
    { id: 'vencidos', from: -60, to: -1, color: '#A3245A', light: false },
    { id: '0', from: 0, to: 3, color: '#EC3B6C', light: false },
    { id: '5', from: 4, to: 9, color: '#FF7A3D', light: true },
    { id: '15', from: 10, to: 24, color: '#FFB547', light: true },
    { id: '30', from: 25, to: 45, color: '#3FC9C0', light: true },
    { id: '45', from: 46, to: 60, color: '#2A6FDB', light: false },
]

export const AGING = DUE_RANGES.filter((range) => !range.neutral).map((range) => ({
    id: range.id,
    label: range.label || `${range.from} a ${range.to} días`,
    color: range.color,
    test: (days) => days >= range.from && days <= range.to,
}))

export const PAYMENT_METHODS = ['Transferencia', 'Tarjeta', 'Depósito en ventanilla', 'Cheque']

export const CONDUCTO_LABELS = {
    CXC: 'Agente',
    CAT: 'Domiciliación',
    CUT: 'Tarjeta',
}

export const isoDate = (date) => toIsoDate(date)

export const shortDate = (value) => formatCompactDate(value) || '—'

export const money = (value) => Number(value || 0).toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})

export const moneyCompact = (value) => {
    const amount = Number(value || 0)
    if (Math.abs(amount) >= 1000000) {
        return `$${(amount / 1000000).toLocaleString('es-MX', { maximumFractionDigits: 1 })} M`
    }
    return money(amount)
}

export const parseAmount = (value) => {
    const cleaned = String(value ?? '').replace(/[^0-9.]/g, '')
    const amount = Number.parseFloat(cleaned)
    return Number.isFinite(amount) ? amount : 0
}

export const personName = (cliente) => [cliente?.nombres, cliente?.apPat, cliente?.apMat].filter(Boolean).join(' ')

export const conductoLabel = (recibo) => {
    const raw = recibo?.poliza?.conducto || recibo?.poliza?.conductoDescripcion
    if (!raw) return ''
    return CONDUCTO_LABELS[raw] || String(raw)
}

export const agingOf = (days) => AGING.find((bucket) => bucket.test(days)) || AGING[AGING.length - 1]

export const dueRangeOf = (days) => {
    const colored = DUE_RANGES.filter((range) => !range.neutral)
    if (days == null || !colored.length) return null
    const match = colored.find((range) => days >= range.from && days <= range.to)
    if (match) return match
    if (days < colored[0].from) return colored[0]
    return colored[colored.length - 1]
}

export const dueDaysLabel = (days) => {
    if (days == null) return '—'
    if (days > 0) return `${days} ${days === 1 ? 'día' : 'días'}`
    if (days === 0) return 'Hoy'
    const late = Math.abs(days)
    return `Vencido ${late} ${late === 1 ? 'día' : 'días'}`
}

export const delayBadge = (days) => {
    if (days == null) return { label: '—', className: 'bg-k-line text-k-ink-2' }
    if (days > 0) return { label: `Vence en ${days} ${days === 1 ? 'día' : 'días'}`, className: 'bg-k-accent-soft text-k-accent-ink' }
    if (days === 0) return { label: 'Vence hoy', className: 'bg-tone-orange text-tone-orange-ink' }
    const late = Math.abs(days)
    const className = late <= 15 ? 'bg-tone-orange text-tone-orange-ink' : 'bg-k-danger-soft text-k-danger-ink'
    return { label: `${late} ${late === 1 ? 'día' : 'días'}`, className }
}

export const coverageNote = (fecVigA) => {
    const days = daysFromToday(fecVigA)
    if (days == null) return { text: '', className: 'text-k-muted', note: '', noteClass: 'text-k-muted' }
    if (days < 0) {
        const late = Math.abs(days)
        return {
            className: 'font-bold text-k-danger',
            note: `Sin cobertura hace ${late} ${late === 1 ? 'día' : 'días'}`,
            noteClass: 'text-k-danger',
        }
    }
    if (days <= 15) {
        return {
            className: 'font-bold text-tone-orange-ink',
            note: `Quedan ${days} ${days === 1 ? 'día' : 'días'}`,
            noteClass: 'text-tone-orange-ink',
        }
    }
    return {
        className: 'font-semibold text-k-ink',
        note: `Quedan ${days} días`,
        noteClass: 'text-k-muted',
    }
}

export const isPeso = (moneda) => {
    const name = String(moneda?.moneda || '').toLowerCase()
    const code = String(moneda?.abreviacion || '').toUpperCase()
    return !moneda || name === 'peso' || code === 'MXN' || code === 'MN'
}

export const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

export const userEmail = (user) => {
    const value = user?.correo
    if (!value) return ''
    if (typeof value === 'string') return value
    return value.correo || value.email || ''
}

export const clientEmails = (cliente) => (cliente?.correos || [])
    .map((item) => item?.correo)
    .filter((value) => String(value || '').trim())

export const clientPhone = (cliente) => (cliente?.celulares || [])
    .map((item) => item?.numero)
    .find((value) => String(value || '').trim()) || ''
